/* KIZUNATAG raster editor controller.
 * Drives the same maker DOM (tabs / parts / options / preview / form / cart) as
 * kizunatag-maker.js, but renders the pet from the hand-drawn JPG parts
 * (assets/<species>/...) instead of the prototype line-art SVG set. The parts are
 * matted (white -> transparent, lines -> ink), anchored and composited into a
 * single PNG that is placed into the 0 0 500 500 product canvas via DATA.petArea.
 * This is the same compositing pipeline as <species>/compose.html, wrapped in the
 * full commerce shell. When this controller owns a [data-kz-maker] root, the SVG
 * controller (kizunatag-maker.js) skips it.
 */
(function () {
  'use strict';

  var VERSION = 'raster-v2-20260609-muzzle';
  var DATA = window.KIZUNATAG_DATA || {};
  /* 写真から線画作成 加工費（円）: configurable.
   * PHOTO_FEE = 実際に課金する金額（= Shopify photo variant の差額に合わせる）
   * PHOTO_FEE_REGULAR = 通常価格（取り消し線表示用） / PHOTO_PROMO = オープン記念価格を表示するか */
  var PHOTO_FEE = 1000;
  var PHOTO_FEE_REGULAR = 1500;
  var PHOTO_PROMO = true;
  var PHOTO_MAX_BYTES = 10 * 1024 * 1024;
  var PHOTO_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  /* Per-section overrides come from Liquid (data-photo-fee / data-photo-regular-fee /
   * data-photo-promo) so each product can set its own price. Falls back to the constants. */
  function photoFee(root) { var v = root && parseInt(root.dataset.photoFee, 10); return (v || v === 0) && !isNaN(v) ? v : PHOTO_FEE; }
  function photoRegularFee(root) { var v = root && parseInt(root.dataset.photoRegularFee, 10); return (v || v === 0) && !isNaN(v) ? v : PHOTO_FEE_REGULAR; }
  function photoPromo(root) { var v = root && root.dataset.photoPromo; if (v == null || v === '') return PHOTO_PROMO; return v === '1' || v === 'true'; }

  /* ---------- raster part specs (mirror of <species>/compose.html) ---------- */
  function range(a, b) { var r = []; for (var n = a; n <= b; n++) r.push(n); return r; }

  var SPECS = {
    dog: {
      alpha: 100,
      tabs: ['face', 'pattern', 'eyes', 'muzzle', 'whiskers', 'decoration'],
      labels: { face: 'お顔', pattern: '模様', eyes: 'おめめ', muzzle: '口元', whiskers: 'おひげ', decoration: '飾り' },
      names: {
        face: { 1: 'たれ耳タイプ', 2: 'たれ耳・外ハネ', 3: 'ふわもこ', 4: 'シュナウザー風', 5: '丸顔たれ耳', 6: 'すっきりたれ耳', 7: 'ブチ柄たれ耳', 8: 'ロング耳', 9: 'ねこちゃん風', 10: 'ふわふわたれ耳', 11: 'ブチ柄', 12: 'ふさふさ長毛', 13: 'ダックス風', 14: 'トイプードル風', 15: 'シュナウザー風・丸ひげ' },
        pattern: { 1: 'そばかす', 3: 'まだら', 4: 'まゆ点', 5: 'おでこ', 7: '耳まわり' },
        eyes: { 1: 'まんまる', 2: 'きらきら', 3: 'つぶら', 4: 'ウインク', 5: 'たれ目', 6: 'つり目', 7: 'やさしい', 8: 'まんまる大', 9: 'くりっと', 10: 'つぶら小', 11: 'まる（線）' },
        muzzle: { 1: 'えがお', 2: 'とじ口', 3: 'みじかめ', 4: 'やさしい', 5: 'ふんわり', 6: 'ちいさなスマイル', 7: 'しっかり', 8: 'ぺろり', 9: 'まんまる' },
        whiskers: { 1: 'ふつう', 2: 'ひかえめ', 3: 'つぶつぶ', 4: 'ハの字' },
        decoration: { 1: '蝶ネクタイ', 2: 'リボン', 3: 'バンダナ', 4: '鈴付き首輪', 5: '首輪', 6: 'ネクタイ', 7: 'お花', 8: '星チャーム', 9: '小さめリボン', 10: 'めがね' }
      },
      /* flat asset names (Shopify assets/ is a flat directory).
       * muzzle (口元 = nose + philtrum + mouth) is a clean line-art PNG. */
      file: function (cat, n) { return cat === 'muzzle' ? ('dog-muzzle' + n + '.png') : ('dog-' + cat + n + '.jpg'); },
      defaults: { face: 1, pattern: 0, eyes: 1, muzzle: 1, whiskers: 0, decoration: 0 },
      nums: { face: range(1, 15), pattern: [1, 3, 4, 5, 7], eyes: range(1, 11), muzzle: range(1, 9), whiskers: range(1, 4), decoration: range(1, 10) },
      anchor: {
        face: { cx: 60, cy: 54, tw: 88, th: 84 },
        pattern: { cx: 60, cy: 54, tw: 88, th: 84 },
        eyes: { cx: 60, cy: 47, tw: 42, th: 22 },
        muzzle: { cx: 60, cy: 68, tw: 34, th: 30 },
        whiskers: { cx: 60, cy: 69, tw: 96, th: 22 },
        decoration: { cx: 60, cy: 92, tw: 34, th: 26 }
      },
      over: {
        /* 小さめ＆控えめに：主役（お顔・おめめ・お鼻）に重ならないよう、
         * 額やほお上の余白へ自然に散らす。 */
        pattern: {
          1: { cx: 60, cy: 58, tw: 38, th: 11 },   /* そばかす: 両ほお・鼻先 */
          3: { cx: 60, cy: 42, tw: 28, th: 16 },   /* まだら: 額に散らばり */
          4: { cx: 60, cy: 42, tw: 24, th: 7 },    /* まゆ点: 目の上 */
          5: { cx: 60, cy: 38, tw: 9, th: 11 },    /* おでこ: 額の中央 */
          7: { cx: 60, cy: 37, tw: 18, th: 15 }    /* 耳まわり: 額上部 */
        },
        decoration: {
          1: { cx: 60, cy: 92, tw: 38, th: 22, pin: 'top' }, 2: { cx: 60, cy: 92, tw: 38, th: 24, pin: 'top' },
          3: { cx: 60, cy: 91, tw: 56, th: 30, pin: 'top' }, 4: { cx: 60, cy: 92, tw: 54, th: 28, pin: 'top' },
          5: { cx: 60, cy: 92, tw: 56, th: 26, pin: 'top' }, 6: { cx: 60, cy: 92, tw: 24, th: 36, pin: 'top' },
          7: { cx: 86, cy: 30, tw: 26, th: 24 }, 8: { cx: 60, cy: 92, tw: 24, th: 34, pin: 'top' },
          9: { cx: 84, cy: 30, tw: 26, th: 20 }, 10: { cx: 60, cy: 47, tw: 52, th: 22 }
        }
      },
      decoFront: { 7: 1, 9: 1, 10: 1 },
      /* これらのお顔は線画自体にブチ柄が入っているため、追加の模様は重ねない。 */
      selfPattern: [7, 11]
    },
    cat: {
      alpha: 12,
      tabs: ['face', 'pattern', 'eyes', 'muzzle', 'whiskers', 'decoration'],
      labels: { face: 'お顔', pattern: '模様', eyes: 'おめめ', muzzle: '口元', whiskers: 'おひげ', decoration: '飾り' },
      names: {
        face: { 1: 'ふつう', 2: 'とがり耳', 3: 'まる顔', 4: 'とがり耳・面長', 5: '大きめ耳', 6: 'まる耳', 7: '逆三角', 8: 'まる顔ワイド', 9: 'ふわふわ', 10: 'まるみ', 11: 'まんまる', 12: 'ふっくら' },
        pattern: { 3: '三毛', 4: 'キジトラ', 5: 'サバトラ', 6: '茶トラ', 7: 'ぶち', 10: 'ポイント', 11: 'うす模様', 12: '耳・ほお色' },
        eyes: { 1: 'まんまる', 2: 'きらきら', 3: 'やさしい', 4: 'つり目', 5: 'くりくり', 6: 'ねむり目', 7: 'きりっと', 8: 'アーモンド', 9: 'まんまる大', 10: 'きらきら大', 11: 'つぶら', 12: 'まんまる丸' },
        muzzle: { 1: 'ふんわり', 2: 'にっこり', 3: 'おすまし', 4: 'ちいさめ', 5: 'ハート鼻', 6: 'まる口', 7: 'きりっと', 8: 'やわらか', 9: 'ねむり' },
        whiskers: { 1: 'ふつう', 2: 'ぴんと', 3: 'たれ', 4: 'ながめ', 5: 'ハの字', 6: 'ひかえめ', 7: 'すっと', 8: 'みじか', 9: 'ぴん', 10: 'ふさ', 11: 'すらり', 12: 'やわらか' },
        decoration: { 1: '大きめリボン', 2: '蝶ネクタイ', 3: '鈴付き首輪', 4: 'お花', 5: '王冠', 6: 'ハット', 7: 'バンダナ', 8: '星チャーム', 9: 'ハート', 10: 'ティアラ', 11: 'ネックレス' }
      },
      /* flat asset names (Shopify assets/ is a flat directory).
       * muzzle (口元 = nose + philtrum + mouth) is a clean line-art PNG. */
      file: function (cat, n) { return cat === 'muzzle' ? ('cat-muzzle' + n + '.png') : ('cat-' + cat + n + '.jpg'); },
      defaults: { face: 1, pattern: 0, eyes: 1, muzzle: 1, whiskers: 0, decoration: 0 },
      nums: { face: range(1, 12), pattern: [3, 4, 5, 6, 7, 10, 11, 12], eyes: range(1, 12), muzzle: range(1, 9), whiskers: range(1, 12), decoration: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11] },
      anchor: {
        face: { cx: 60, cy: 54, tw: 86, th: 82 },
        pattern: { cx: 60, cy: 54, tw: 86, th: 82 },
        eyes: { cx: 60, cy: 48, tw: 50, th: 26 },
        muzzle: { cx: 60, cy: 70, tw: 26, th: 30 },
        whiskers: { cx: 60, cy: 63, tw: 92, th: 26 },
        decoration: { cx: 60, cy: 94, tw: 28, th: 20 }
      },
      over: {
        /* 額の縦縞・Ｍ字模様がおめめに被らないよう、額の上側へ少しだけ持ち上げる。
         * （三毛/ぶち/ポイントなどの面塗りタイプはお顔に合わせるため anchor のまま） */
        pattern: {
          4: { cx: 60, cy: 47, tw: 86, th: 82 },   /* キジトラ: Ｍ字を額へ */
          5: { cx: 60, cy: 47, tw: 86, th: 82 },   /* サバトラ: Ｍ字を額へ */
          6: { cx: 60, cy: 43, tw: 86, th: 82 },   /* 茶トラ: 縦縞を額へ（被り大） */
          12: { cx: 60, cy: 50, tw: 86, th: 82 }   /* 耳・ほお色: 縦縞を少し上へ */
        },
        /* head-top: crown/hat/tiara | ear/side: ribbon/flower/heart
         * neck: bowtie/collar/bandana/necklace | hang: star (pin top at chin) */
        decoration: {
          1: { cx: 80, cy: 27, tw: 36, th: 30 },
          2: { cx: 60, cy: 91, tw: 38, th: 22, pin: 'top' },
          3: { cx: 60, cy: 91, tw: 54, th: 28, pin: 'top' },
          4: { cx: 86, cy: 30, tw: 24, th: 24 },
          5: { cx: 60, cy: 18, tw: 42, th: 26 },
          6: { cx: 60, cy: 17, tw: 56, th: 30 },
          7: { cx: 60, cy: 90, tw: 56, th: 30, pin: 'top' },
          8: { cx: 60, cy: 91, tw: 24, th: 34, pin: 'top' },
          9: { cx: 84, cy: 34, tw: 26, th: 18 },
          10: { cx: 60, cy: 19, tw: 56, th: 22 },
          11: { cx: 60, cy: 93, tw: 54, th: 26, pin: 'top' }
        }
      },
      decoFront: 'all' /* cat compose keeps decoration on top (last layer) */
    }
  };
  var VB = { w: 120, h: 125 };
  var PET_BASE = ['face', 'pattern', 'eyes', 'muzzle', 'whiskers']; /* deco handled separately */
  window.KIZUNATAG_RASTER = SPECS;

  /* ---------- helpers ---------- */
  function $(root, sel) { return root.querySelector(sel); }
  function $$(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }
  function species(root) { return root.dataset.species === 'dog' ? 'dog' : 'cat'; }
  function spec(root) { return SPECS[species(root)]; }
  function findById(coll, id) {
    var list = DATA[coll] || [];
    for (var i = 0; i < list.length; i++) { if (list[i].id === id) return list[i]; }
    return list[0] || null;
  }
  function material(root) { return findById('materials', stateOf(root).material) || {}; }
  function product(root) { return findById('products', stateOf(root).product) || {}; }
  /* Map the generator's selected product to a Shopify variant by matching the
   * product label (e.g. 迷子札 / キーホルダー) against the store product titles
   * supplied via data-product-map. Returns {variant, photoVariant} or null. */
  function productMap(root) {
    if (root._kzProductMap) return root._kzProductMap;
    var map = [];
    try { map = JSON.parse(root.dataset.productMap || '[]') || []; } catch (e) { map = []; }
    root._kzProductMap = map;
    return map;
  }
  function matchVariant(root) {
    var label = (product(root) || {}).label || '';
    if (!label) return null;
    var map = productMap(root);
    for (var i = 0; i < map.length; i++) {
      if (map[i] && map[i].title && map[i].title.indexOf(label) >= 0) return map[i];
    }
    return null;
  }
  function catalog() { return window.KIZUNATAG_CATALOG || {}; }
  function sizeScale(root) {
    var st = stateOf(root), C = catalog();
    var s = C.findSize ? C.findSize(st.product, st.size) : findById('sizes', st.size);
    return s && s.scale ? s.scale : 1;
  }
  function sizeSummaryLabel(root) {
    var st = stateOf(root), C = catalog();
    return C.sizeSummary ? C.sizeSummary(st.product, st.size) : ((findById('sizes', st.size) || {}).label || '');
  }
  function fontFamily(root) { var f = findById('fonts', stateOf(root).font); return f && f.family ? f.family : 'sans-serif'; }
  function isFront(root, n) { var df = spec(root).decoFront; return df === 'all' ? true : !!df[n]; }
  function i18() { return window.KIZUNATAG_I18N || null; }
  function T(key, ja) { var i = i18(); return i ? i.t(key, ja) : ja; }
  function tf(key, jaTpl, val) { return String(T(key, jaTpl)).replace('%s', val); }
  function catLabel(root, cat, ja) { var i = i18(); return i ? i.cat(cat, ja) : ja; }
  function partNameJa(root, cat, n) { var m = spec(root).names; return (m && m[cat] && m[cat][n]) || ''; }
  function partName(root, cat, n) { var ja = partNameJa(root, cat, n); var i = i18(); return i ? i.partName(species(root), cat, n, ja) : ja; }
  function partLabel(root, cat, n) { var nm = partName(root, cat, n); return '#' + n + (nm ? ' ' + nm : ''); }
  /* Japanese-only label for order/cart properties (merchant reads orders in JP). */
  function selectedLabelJa(root, key) {
    var state = stateOf(root);
    if (key === 'text') return state.name || '';
    var n = state.sel[key];
    if (!n) return 'なし';
    var nm = partNameJa(root, key, n);
    return '#' + n + (nm ? ' ' + nm : '');
  }
  /* Localized catalog label helpers (UI only). */
  function prodLabelUi(root, ja) { var i = i18(); return i ? i.label('products', stateOf(root).product, ja) : ja; }
  function matLabelUi(root, ja) { var i = i18(); return i ? i.label('materials', stateOf(root).material, ja) : ja; }
  function sizeSummaryUi(root) { var i = i18(); var s = sizeSummaryLabel(root); return i ? i.size(s) : s; }

  /* asset base derived from this script's URL (handles /assets/<sub>/file) */
  var ASSET_BASE = (function () {
    var s = document.querySelector('script[src*="kizunatag-raster.js"]');
    var src = (s && s.src) || '';
    return src.indexOf('/assets/') > -1 ? src.split('/assets/')[0] + '/assets/' : './assets/';
  })();
  function partUrl(root, cat, n) { return ASSET_BASE + encodeURI(spec(root).file(cat, n)); }
  function assetUrl(file) { return ASSET_BASE + file; }

  /* ---------- state ---------- */
  var STATE = new WeakMap();
  var UID = 0;
  function stateOf(root) { return STATE.get(root); }
  function initState(root) {
    var sp = spec(root), d = sp.defaults;
    var state = {
      uid: ++UID,
      active: 'face',
      sel: { face: d.face, pattern: d.pattern, eyes: d.eyes, muzzle: d.muzzle, whiskers: d.whiskers, decoration: d.decoration },
      product: 'tag', material: 'wood', size: 'm', font: 'rounded',
      name: 'KIZUNATAG', phone: '090-0000-0000', message: 'いつも一緒だよ',
      petPng: '', petSilPng: '', petAR: 0, petToken: 0,
      faceMode: 'preset', photoFile: null, photoPreviewUrl: '', accepted: false, photoError: ''
    };
    STATE.set(root, state);
    return state;
  }
  function clearPhoto(state) {
    if (state.photoPreviewUrl) { try { URL.revokeObjectURL(state.photoPreviewUrl); } catch (e) { } }
    state.photoFile = null; state.photoPreviewUrl = ''; state.accepted = false; state.photoError = '';
  }

  /* ---------- image matting (white -> transparent, lines -> ink) ---------- */
  var procCache = new Map();
  function processImage(url, alphaCut, ink) {
    var key = url + '|' + alphaCut + '|' + ink.join(',');
    if (procCache.has(key)) return procCache.get(key);
    var p = new Promise(function (resolve) {
      var im = new Image();
      try { im.crossOrigin = 'anonymous'; } catch (e) { }
      im.onload = function () {
        var c = document.createElement('canvas');
        c.width = im.naturalWidth; c.height = im.naturalHeight;
        var x = c.getContext('2d');
        x.drawImage(im, 0, 0);
        var d, a;
        try { d = x.getImageData(0, 0, c.width, c.height); a = d.data; }
        catch (e) { resolve({ src: url, ccx: 0.5, ccy: 0.5, bw: 1, car: 1, iw: c.width, ih: c.height, raw: true }); return; }
        var minX = c.width, minY = c.height, maxX = 0, maxY = 0, has = false;
        for (var i = 0; i < a.length; i += 4) {
          var lum = 0.299 * a[i] + 0.587 * a[i + 1] + 0.114 * a[i + 2];
          var alpha = (255 - lum) * 1.25;
          if (alpha > 255) alpha = 255;
          if (alpha < alphaCut) alpha = 0;
          a[i] = ink[0]; a[i + 1] = ink[1]; a[i + 2] = ink[2]; a[i + 3] = alpha;
          if (alpha > 40) {
            var idx = i / 4, px = idx % c.width, py = (idx / c.width) | 0;
            if (px < minX) minX = px; if (px > maxX) maxX = px;
            if (py < minY) minY = py; if (py > maxY) maxY = py; has = true;
          }
        }
        x.putImageData(d, 0, 0);
        resolve({
          src: c.toDataURL('image/png'),
          ccx: has ? ((minX + maxX) / 2) / c.width : 0.5,
          ccy: has ? ((minY + maxY) / 2) / c.height : 0.5,
          topf: has ? minY / c.height : 0,
          botf: has ? (maxY + 1) / c.height : 1,
          bw: has ? (maxX - minX + 1) / c.width : 1,
          car: has ? (maxY - minY + 1) / (maxX - minX + 1) : 1,
          iw: c.width, ih: c.height
        });
      };
      im.onerror = function () { resolve({ src: '', ccx: 0.5, ccy: 0.5, bw: 1, car: 1, iw: 1, ih: 1 }); };
      im.src = url;
    });
    procCache.set(key, p);
    return p;
  }
  function loadImg(src) { return new Promise(function (r) { var i = new Image(); i.onload = function () { r(i); }; i.onerror = function () { r(null); }; i.src = src; }); }
  function anchorOf(root, cat, n) { var sp = spec(root); return (sp.over[cat] && sp.over[cat][n]) || sp.anchor[cat]; }
  function fitImageWidth(a, r) { var contentW = Math.min(a.tw, a.th / (r.car || 1)); return contentW / (r.bw || 1); }
  function inkRgb(root) {
    var hex = (material(root).ink || '#2f2119').replace('#', '');
    if (hex.length === 3) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    var n = parseInt(hex, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }

  /* お顔自体に柄が入っているタイプ（ブチ柄など）は、追加の模様を重ねない。 */
  function faceHasOwnPattern(root) {
    var sp = spec(root), face = stateOf(root).sel.face;
    return !!(sp.selfPattern && sp.selfPattern.indexOf(face) > -1);
  }
  /* composite the selected parts onto a transparent canvas (VB * scale) */
  async function composeCanvas(root, scale) {
    var sp = spec(root), sel = stateOf(root).sel, ink = inkRgb(root);
    var c = document.createElement('canvas');
    c.width = VB.w * scale; c.height = VB.h * scale;
    var ctx = c.getContext('2d');
    ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
    var dn = sel.decoration;
    var order = [];
    if (dn && !isFront(root, dn)) order.push('decoration');
    PET_BASE.forEach(function (cat) { order.push(cat); });
    if (dn && isFront(root, dn)) order.push('decoration');
    for (var k = 0; k < order.length; k++) {
      var cat = order[k], n = sel[cat];
      if (!n) continue;
      if (cat === 'pattern' && faceHasOwnPattern(root)) continue;
      var a = anchorOf(root, cat, n);
      var r = await processImage(partUrl(root, cat, n), sp.alpha, ink);
      if (!r.src) continue;
      var im = await loadImg(r.src); if (!im) continue;
      var w = fitImageWidth(a, r) * scale;
      var h = w * (im.naturalHeight / im.naturalWidth);
      var pinY = a.pin === 'top' ? (r.topf || 0)
        : a.pin === 'bottom' ? (r.botf != null ? r.botf : 1)
          : r.ccy;
      ctx.drawImage(im, a.cx * scale - r.ccx * w, a.cy * scale - pinY * h, w, h);
    }
    return c;
  }
  async function composePng(root, scale) { return (await composeCanvas(root, scale)).toDataURL('image/png'); }
  function dataURLToBlob(dataURL) {
    var parts = String(dataURL).split(',');
    if (parts.length < 2) return null;
    var mime = (parts[0].match(/data:([^;]+)/) || [])[1] || 'image/png';
    var bin = atob(parts[1]), len = bin.length, arr = new Uint8Array(len);
    for (var i = 0; i < len; i++) arr[i] = bin.charCodeAt(i);
    return new Blob([arr], { type: mime });
  }
  /* Recolour a single-tone line-art image to pure black while keeping its alpha,
   * so the production file is high-contrast for laser engraving. */
  function recolorBlack(img) {
    var w = img.naturalWidth || img.width, h = img.naturalHeight || img.height;
    var c = document.createElement('canvas'); c.width = w; c.height = h;
    var x = c.getContext('2d');
    x.drawImage(img, 0, 0, w, h);
    x.globalCompositeOperation = 'source-in';
    x.fillStyle = '#000'; x.fillRect(0, 0, w, h);
    return c;
  }
  /* Production engraving file: pure-black pet line art (plus the engraved name /
   * message for non-contour products, matching the preview) on a transparent
   * 2000px canvas in the product (500x500) coordinate space. No product body,
   * material texture or cut guide, so it is ready to send to the laser. */
  async function composeEngravingPng(root) {
    var state = stateOf(root);
    if (state.faceMode === 'photo' || !state.petPng) return '';
    var OUT = 2000, S = OUT / 500, sc = sizeScale(root);
    var c = document.createElement('canvas'); c.width = OUT; c.height = OUT;
    var ctx = c.getContext('2d');
    ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
    ctx.scale(S, S);
    ctx.translate(250, 250); ctx.scale(sc, sc); ctx.translate(-250, -250);
    var im = await loadImg(state.petPng);
    if (im) {
      var ar = state.petAR || (im.naturalHeight / im.naturalWidth);
      var b = petBox(root);
      var w = b.w, h = w * ar;
      if (h > b.h) { h = b.h; w = h / ar; }
      ctx.drawImage(recolorBlack(im), b.cx - w / 2, b.cy - h / 2, w, h);
    }
    if (!productIsContour(root)) {
      var ff = fontFamily(root), maxW = plateGeom(root).w * 0.82;
      ctx.fillStyle = '#000'; ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic';
      textLines(root).forEach(function (l) {
        ctx.font = l.weight + ' ' + l.size + 'px ' + ff;
        try { ctx.letterSpacing = l.ls + 'px'; } catch (e) { }
        var m = ctx.measureText(l.t).width;
        if (m > maxW) {
          ctx.save(); ctx.translate(250, l.y); ctx.scale(maxW / m, 1);
          ctx.fillText(l.t, 0, 0); ctx.restore();
        } else { ctx.fillText(l.t, 250, l.y); }
      });
      try { ctx.letterSpacing = '0px'; } catch (e) { }
    }
    return c.toDataURL('image/png');
  }
  /* Cut-line file for contour products (keyholder / charm / nameplate): the solid
   * pet silhouette placed at the same position as the engraving, so the operator
   * can cut the acrylic/wood to the pet outline. Null for standard shapes. */
  async function composeCutPng(root) {
    var state = stateOf(root);
    if (!productIsContour(root) || !state.petSilPng) return '';
    var OUT = 2000, S = OUT / 500, sc = sizeScale(root);
    var c = document.createElement('canvas'); c.width = OUT; c.height = OUT;
    var ctx = c.getContext('2d');
    ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
    ctx.scale(S, S);
    ctx.translate(250, 250); ctx.scale(sc, sc); ctx.translate(-250, -250);
    var im = await loadImg(state.petSilPng);
    if (!im) return '';
    var ar = state.petAR || (im.naturalHeight / im.naturalWidth);
    var b = petBox(root);
    var w = b.w, h = w * ar;
    if (h > b.h) { h = b.h; w = h / ar; }
    ctx.drawImage(im, b.cx - w / 2, b.cy - h / 2, w, h);
    return c.toDataURL('image/png');
  }

  /* Trim a composed canvas to its ink bounding box, returning a centered PNG and
   * its aspect ratio so the preview can fill the plate (no surrounding whitespace). */
  function trimCanvas(canvas) {
    var ctx = canvas.getContext('2d'), W = canvas.width, H = canvas.height, data;
    try { data = ctx.getImageData(0, 0, W, H).data; } catch (e) { return { url: canvas.toDataURL('image/png'), ar: H / W }; }
    var minX = W, minY = H, maxX = -1, maxY = -1;
    for (var y = 0; y < H; y++) {
      for (var x = 0; x < W; x++) {
        if (data[(y * W + x) * 4 + 3] > 16) {
          if (x < minX) minX = x; if (x > maxX) maxX = x;
          if (y < minY) minY = y; if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX < minX) return { url: canvas.toDataURL('image/png'), ar: H / W };
    var w = maxX - minX + 1, h = maxY - minY + 1;
    var t = document.createElement('canvas'); t.width = w; t.height = h;
    t.getContext('2d').drawImage(canvas, minX, minY, w, h, 0, 0, w, h);
    return { url: t.toDataURL('image/png'), ar: h / w };
  }

  /* ---------- contour (die-cut) silhouette ----------
   * For acrylic charms / keychains and the wood name block, the real product is
   * cut to the pet outline. We build a SOLID silhouette mask from the composed
   * line art: flood-fill the background from the edges over non-ink pixels; every
   * pixel that the flood can't reach (the ink lines + everything they enclose) is
   * "inside" the pet. The ink is dilated first so small gaps in the outline don't
   * leak. Returns aligned crops of both the line art and the silhouette. */
  function dilateMask(mask, W, H, passes) {
    for (var p = 0; p < passes; p++) {
      var next = mask.slice(0);
      for (var y = 0; y < H; y++) {
        for (var x = 0; x < W; x++) {
          var i = y * W + x;
          if (mask[i]) continue;
          if ((x > 0 && mask[i - 1]) || (x < W - 1 && mask[i + 1]) ||
            (y > 0 && mask[i - W]) || (y < H - 1 && mask[i + W])) next[i] = 1;
        }
      }
      mask = next;
    }
    return mask;
  }
  function silhouetteFrom(canvas) {
    var W = canvas.width, H = canvas.height, data;
    try { data = canvas.getContext('2d').getImageData(0, 0, W, H).data; }
    catch (e) { return null; }
    var N = W * H, ink = new Uint8Array(N), i;
    for (i = 0; i < N; i++) ink[i] = data[i * 4 + 3] > 24 ? 1 : 0;
    ink = dilateMask(ink, W, H, 2); /* seal small outline gaps */
    var outside = new Uint8Array(N), stack = [], x, y, idx;
    function push(px, py) {
      if (px < 0 || py < 0 || px >= W || py >= H) return;
      var k = py * W + px;
      if (outside[k] || ink[k]) return;
      outside[k] = 1; stack.push(k);
    }
    for (x = 0; x < W; x++) { push(x, 0); push(x, H - 1); }
    for (y = 0; y < H; y++) { push(0, y); push(W - 1, y); }
    while (stack.length) { idx = stack.pop(); x = idx % W; y = (idx / W) | 0; push(x - 1, y); push(x + 1, y); push(x, y - 1); push(x, y + 1); }
    var out = document.createElement('canvas'); out.width = W; out.height = H;
    var octx = out.getContext('2d'), od = octx.createImageData(W, H), a = od.data;
    var minX = W, minY = H, maxX = -1, maxY = -1, has = false;
    for (i = 0; i < N; i++) {
      if (!outside[i]) {
        a[i * 4] = 0; a[i * 4 + 1] = 0; a[i * 4 + 2] = 0; a[i * 4 + 3] = 255;
        x = i % W; y = (i / W) | 0;
        if (x < minX) minX = x; if (x > maxX) maxX = x;
        if (y < minY) minY = y; if (y > maxY) maxY = y; has = true;
      }
    }
    octx.putImageData(od, 0, 0);
    if (!has) return null;
    return { canvas: out, bb: { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 } };
  }
  function cropDataUrl(canvas, bb) {
    var t = document.createElement('canvas'); t.width = bb.w; t.height = bb.h;
    t.getContext('2d').drawImage(canvas, bb.x, bb.y, bb.w, bb.h, 0, 0, bb.w, bb.h);
    return t.toDataURL('image/png');
  }

  /* ---------- product canvas (0 0 500 500), reused from maker visuals ---------- */
  /* Per-product silhouette table (keyed by product id). The product canvas is
   * 500x500; every shape is centered around (250,256). hang controls the top
   * fitting (hole / ring / cord / screws / base / none). This is what makes each
   * product look like the real item instead of one generic rounded tag. */
  var SHAPE_GEO = {
    tag:        { w: 336, h: 336, r: 168, hang: 'ring' },
    keyholder:  { w: 320, h: 388, hang: 'ring', contour: true },
    charm:      { w: 312, h: 392, hang: 'bead', contour: true },
    plate:      { w: 452, h: 244, r: 24,  hang: 'none' },
    light:      { w: 420, h: 300, hang: 'base', ellipse: true },
    roomsign:   { w: 410, h: 288, r: 24,  hang: 'cord' },
    memorial:   { w: 384, h: 300, r: 16,  hang: 'base' },
    ornament:   { w: 344, h: 344, r: 172, hang: 'hole' },
    leathertag: { w: 320, h: 320, r: 160, hang: 'hole', stitch: true },
    coaster:    { w: 360, h: 360, r: 180, hang: 'none', inner: true },
    doorplate:  { w: 452, h: 252, hang: 'cord', ellipse: true },
    nameplate:  { w: 392, h: 356, hang: 'standbase', contour: true }
  };
  function shapeKeyOf(prod) {
    var id = prod && prod.id;
    if (id && SHAPE_GEO[id]) return id;
    return (prod && prod.shape && SHAPE_GEO[prod.shape]) ? prod.shape : 'tag';
  }
  function shapeGeo(root) { return SHAPE_GEO[shapeKeyOf(product(root))] || SHAPE_GEO.tag; }
  function productIsContour(root) { var s = shapeGeo(root); return !!(s && s.contour); }
  function plateGeom(root) {
    var s = shapeGeo(root), cx = 250, cy = 256;
    return { x: cx - s.w / 2, y: cy - s.h / 2, w: s.w, h: s.h, r: s.r, hang: s.hang, spec: s };
  }
  function roundRect(x, y, w, h, r) {
    return 'M' + (x + r) + ' ' + y + ' H' + (x + w - r) + ' Q' + (x + w) + ' ' + y + ' ' + (x + w) + ' ' + (y + r) +
      ' V' + (y + h - r) + ' Q' + (x + w) + ' ' + (y + h) + ' ' + (x + w - r) + ' ' + (y + h) +
      ' H' + (x + r) + ' Q' + x + ' ' + (y + h) + ' ' + x + ' ' + (y + h - r) +
      ' V' + (y + r) + ' Q' + x + ' ' + y + ' ' + (x + r) + ' ' + y + 'Z';
  }
  /* Solid material fill (avoids referencing data.js gradient ids, which would
   * collide across the desktop/mobile preview SVGs). */
  function matFill(root) {
    var id = stateOf(root).material;
    if (id === 'leather') return '#8d502b';
    if (id === 'acrylic') return '#d9ecef';
    return '#e8c089';
  }
  /* Body outline path for the current shape. Arched (memorial) gets a rounded
   * top + squared bottom; everything else is a rounded rect (a large r turns it
   * into a circle for ornament/charm). */
  function ellipsePath(cx, cy, rx, ry) {
    return 'M' + (cx - rx) + ' ' + cy +
      ' A' + rx + ' ' + ry + ' 0 0 1 ' + (cx + rx) + ' ' + cy +
      ' A' + rx + ' ' + ry + ' 0 0 1 ' + (cx - rx) + ' ' + cy + 'Z';
  }
  function bodyPath(g) {
    if (g.spec && g.spec.ellipse) return ellipsePath(g.x + g.w / 2, g.y + g.h / 2, g.w / 2, g.h / 2);
    if (g.hang === 'arch') {
      var rr = Math.min(g.w / 2, g.h * 0.55);
      return 'M' + g.x + ' ' + (g.y + rr) +
        ' A' + rr + ' ' + rr + ' 0 0 1 ' + (g.x + g.w) + ' ' + (g.y + rr) +
        ' V' + (g.y + g.h - 14) + ' Q' + (g.x + g.w) + ' ' + (g.y + g.h) + ' ' + (g.x + g.w - 14) + ' ' + (g.y + g.h) +
        ' H' + (g.x + 14) + ' Q' + g.x + ' ' + (g.y + g.h) + ' ' + g.x + ' ' + (g.y + g.h - 14) + 'Z';
    }
    return roundRect(g.x, g.y, g.w, g.h, g.r);
  }
  /* Hanging / mounting hardware drawn for each shape so the product reads as the
   * real item (ring for keyholder, cord for room sign, screws for door plate…). */
  function hangSvg(g) {
    var cx = 250;
    if (g.hang === 'hole') return '<circle cx="' + cx + '" cy="' + (g.y + 30) + '" r="12" fill="#5d371f" opacity=".5"/>';
    if (g.hang === 'ring')
      return '<circle cx="' + cx + '" cy="' + (g.y - 18) + '" r="20" fill="none" stroke="url(#kr-metal)" stroke-width="6"/>' +
        '<circle cx="' + cx + '" cy="' + (g.y + 26) + '" r="9" fill="#5d371f" opacity=".45"/>';
    if (g.hang === 'cord') {
      var ell = g.spec && g.spec.ellipse;
      var lx = ell ? (cx - g.w * 0.26) : (g.x + 54);
      var rx = ell ? (cx + g.w * 0.26) : (g.x + g.w - 54);
      var hy = ell ? (g.y + g.h * 0.20) : g.y;
      var ty = hy - 40;
      return '<path d="M' + lx + ' ' + hy + ' L' + cx + ' ' + ty + ' L' + rx + ' ' + hy + '" fill="none" stroke="#9b7b52" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>' +
        '<circle cx="' + cx + '" cy="' + (ty - 8) + '" r="8" fill="none" stroke="url(#kr-metal)" stroke-width="4"/>' +
        '<circle cx="' + lx + '" cy="' + (hy + 14) + '" r="6" fill="#5d371f" opacity=".45"/>' +
        '<circle cx="' + rx + '" cy="' + (hy + 14) + '" r="6" fill="#5d371f" opacity=".45"/>';
    }
    if (g.hang === 'screws') {
      var ins = 30, s = '';
      [[g.x + ins, g.y + ins], [g.x + g.w - ins, g.y + ins], [g.x + ins, g.y + g.h - ins], [g.x + g.w - ins, g.y + g.h - ins]]
        .forEach(function (p) {
          s += '<circle cx="' + p[0] + '" cy="' + p[1] + '" r="9" fill="none" stroke="url(#kr-metal)" stroke-width="3"/>' +
            '<path d="M' + (p[0] - 5) + ' ' + p[1] + ' h10 M' + p[0] + ' ' + (p[1] - 5) + ' v10" stroke="#7a5733" stroke-width="2" stroke-linecap="round"/>';
        });
      return s;
    }
    return '';
  }
  /* Hardware (ring / ball-chain / stand base) for die-cut contour products,
   * positioned around the silhouette box instead of a geometric body. */
  function contourHardware(root) {
    var g = plateGeom(root), b = petBox(root), cx = 250;
    var topY = b.y, botY = b.y + b.h;
    if (g.hang === 'ring') return '<circle cx="' + cx + '" cy="' + (topY - 6) + '" r="20" fill="none" stroke="url(#kr-metal)" stroke-width="6"/>';
    if (g.hang === 'bead') {
      var s = '', k;
      for (k = 0; k < 6; k++) s += '<circle cx="' + cx + '" cy="' + (topY - 8 - k * 9) + '" r="3.4" fill="#cfcdc8"/>';
      s += '<circle cx="' + cx + '" cy="' + (topY - 8 - 6 * 9 - 10) + '" r="9" fill="none" stroke="url(#kr-metal)" stroke-width="3"/>';
      return s;
    }
    if (g.hang === 'standbase') {
      var bw = b.w * 0.66;
      return '<path d="M' + (cx - bw / 2) + ' ' + (botY + 34) + ' L' + (cx - bw / 2 + 18) + ' ' + (botY + 2) + ' H' + (cx + bw / 2 - 18) + ' L' + (cx + bw / 2) + ' ' + (botY + 34) + 'Z" fill="' + matFill(root) + '" stroke="' + (material(root).edge || '#b98243') + '" stroke-width="4"/>';
    }
    return '';
  }
  /* Plain rounded body shown for contour products while the silhouette is still
   * being computed (or in photo mode), so the product is never invisible. */
  function contourFallbackBody(root) {
    var b = petBox(root), mat = material(root);
    var r = Math.min(b.w, b.h) * 0.18;
    return '<path d="' + roundRect(b.x, b.y, b.w, b.h, r) + '" fill="' + matFill(root) +
      '" fill-opacity="' + (mat.opacity == null ? 1 : mat.opacity) + '" stroke="' + (mat.edge || '#b98243') + '" stroke-width="4"/>';
  }
  function productBaseSvg(root) {
    var g = plateGeom(root), mat = material(root);
    if (g.spec && g.spec.contour) return '<g>' + contourHardware(root) + '</g>';
    var d = bodyPath(g);
    var base = '';
    if (g.hang === 'base') {
      var cx = 250, by = g.y + g.h, bw = g.w * 0.46;
      base = '<path d="M' + (cx - bw / 2) + ' ' + (by + 28) + ' L' + (cx - bw / 2 + 16) + ' ' + (by - 4) + ' H' + (cx + bw / 2 - 16) + ' L' + (cx + bw / 2) + ' ' + (by + 28) + 'Z" fill="' + matFill(root) + '" stroke="' + (mat.edge || '#b98243') + '" stroke-width="4"/>';
    }
    var roundShape = g.r >= Math.min(g.w, g.h) / 2 - 2;
    var stitch = g.spec && g.spec.stitch
      ? (roundShape
        ? '<circle cx="250" cy="256" r="' + (Math.min(g.w, g.h) / 2 - 20) + '" fill="none" stroke="#fff6e8" stroke-opacity=".7" stroke-width="2" stroke-dasharray="2 7" stroke-linecap="round"/>'
        : '<path d="' + roundRect(g.x + 18, g.y + 18, g.w - 36, g.h - 36, Math.max(8, g.r - 10)) + '" fill="none" stroke="#fff6e8" stroke-opacity=".7" stroke-width="2" stroke-dasharray="2 7" stroke-linecap="round"/>')
      : '';
    var inner = g.spec && g.spec.inner
      ? '<circle cx="250" cy="256" r="' + (Math.min(g.w, g.h) / 2 - 34) + '" fill="none" stroke="#fff9ef" stroke-opacity=".35" stroke-width="2" stroke-dasharray="5 8"/>'
      : '';
    return '<g>' + base + hangSvg(g) +
      '<path d="' + d + '" fill="' + matFill(root) + '" fill-opacity="' + (mat.opacity == null ? 1 : mat.opacity) + '" stroke="' + (mat.edge || '#b98243') + '" stroke-width="4"/>' +
      '<path d="' + d + '" fill="none" stroke="#fff9ef" stroke-opacity=".30" stroke-width="2"/>' + stitch + inner + '</g>';
  }
  function materialTextureSvg(root) {
    var g = plateGeom(root);
    if (g.spec && g.spec.contour) return '';
    return '<g opacity=".45"><path d="' + bodyPath(g) + '" fill="url(#kr-sheen)"/></g>';
  }
  function cutGuideSvg(root) {
    var g = plateGeom(root), m = 15;
    if (g.spec && g.spec.contour) return '';
    if (g.spec && g.spec.ellipse) {
      return '<g><path d="' + ellipsePath(250, 256, g.w / 2 - m, g.h / 2 - m) +
        '" fill="none" stroke="#ffffff" stroke-opacity=".4" stroke-width="1.4" stroke-dasharray="6 7"/></g>';
    }
    if (g.r >= Math.min(g.w, g.h) / 2 - 2) {
      return '<g><circle cx="250" cy="256" r="' + (Math.min(g.w, g.h) / 2 - m) +
        '" fill="none" stroke="#ffffff" stroke-opacity=".4" stroke-width="1.4" stroke-dasharray="6 7"/></g>';
    }
    return '<g><path d="' + roundRect(g.x + m, g.y + m, g.w - 2 * m, g.h - 2 * m, Math.max(8, g.r - 8)) +
      '" fill="none" stroke="#ffffff" stroke-opacity=".4" stroke-width="1.4" stroke-dasharray="6 7"/></g>';
  }
  /* Drawable band: below the hanging hardware, above the engraved name. Derived
   * from the active shape so the art always sits correctly on each product. */
  function petBox(root) {
    var g = plateGeom(root);
    if (g.spec && g.spec.contour) {
      var ctopGap = g.hang === 'standbase' ? 30 : 66;
      var cbotGap = g.hang === 'standbase' ? 48 : 26;
      var ctop = g.y + ctopGap, cbottom = g.y + g.h - cbotGap, cpad = 20;
      return { x: g.x + cpad, y: ctop, w: g.w - 2 * cpad, h: cbottom - ctop, cx: 250, cy: (ctop + cbottom) / 2 };
    }
    var pad = Math.max(46, g.w * 0.16);
    var x = g.x + pad, x2 = g.x + g.w - pad;
    var topGap = (g.hang === 'hole' || g.hang === 'ring' || g.hang === 'cord' || g.hang === 'arch') ? 64 : 40;
    var top = g.y + topGap;
    var bottom = textLines(root).top - 14;
    if (bottom - top < 90) { top = g.y + 28; bottom = textLines(root).top - 8; }
    return { x: x, y: top, w: x2 - x, h: bottom - top, cx: (x + x2) / 2, cy: (top + bottom) / 2 };
  }
  function nameBaseline(g) { return g.y + g.h - (g.spec && g.spec.baseline ? 40 : 44); }
  function photoPlaceholderSvg(root) {
    var b = petBox(root), mat = material(root), ink = mat.ink || '#2f2119';
    var cx = b.cx, cy = b.cy;
    return '<g data-layer="pet" data-kz-photo-ph>' +
      '<rect x="' + (cx - 96) + '" y="' + (cy - 78) + '" width="192" height="156" rx="16" fill="none" stroke="' + ink + '" stroke-opacity=".5" stroke-width="2.5" stroke-dasharray="8 7"/>' +
      '<circle cx="' + cx + '" cy="' + (cy - 14) + '" r="30" fill="none" stroke="' + ink + '" stroke-opacity=".55" stroke-width="2.5"/>' +
      '<path d="M' + (cx - 18) + ' ' + (cy - 24) + 'l5-7h26l5 7" fill="none" stroke="' + ink + '" stroke-opacity=".55" stroke-width="2.5" stroke-linejoin="round"/>' +
      '<text x="' + cx + '" y="' + (cy + 40) + '" text-anchor="middle" font-size="20" font-weight="700" font-family=\'' + fontFamily(root) + '\' fill="' + ink + '" fill-opacity=".75">' + esc(T('preview_photo_title', '写真から線画を作成')) + '</text>' +
      '<text x="' + cx + '" y="' + (cy + 64) + '" text-anchor="middle" font-size="13" font-family=\'' + fontFamily(root) + '\' fill="' + ink + '" fill-opacity=".6">' + esc(T('preview_photo_sub', 'お写真をもとに制作します')) + '</text>' +
      '</g>';
  }
  function petImageTag(href, x, y, w, h, extra) {
    return '<image ' + (extra || '') + ' href="' + href + '" xlink:href="' + href +
      '" x="' + x.toFixed(1) + '" y="' + y.toFixed(1) + '" width="' + w.toFixed(1) + '" height="' + h.toFixed(1) +
      '" preserveAspectRatio="none"/>';
  }
  function petGroupSvg(root) {
    var state = stateOf(root), contour = productIsContour(root);
    if (state.faceMode === 'photo') return (contour ? contourFallbackBody(root) : '') + photoPlaceholderSvg(root);
    var href = state.petPng || '';
    var ar = state.petAR || (VB.h / VB.w);   // height / width of trimmed art
    var b = petBox(root);
    var w = b.w, h = w * ar;
    if (h > b.h) { h = b.h; w = h / ar; }     // contain inside the band
    var x = b.cx - w / 2, y = b.cy - h / 2;
    if (contour) {
      if (state.petSilPng) {
        var filt = 'filter="url(#kr-sil-' + state.uid + ')"';
        return '<g data-layer="pet">' +
          petImageTag(state.petSilPng, x, y, w, h, filt) +
          petImageTag(href, x, y, w, h, 'data-kz-pet-image') + '</g>';
      }
      return contourFallbackBody(root) + '<g data-layer="pet">' + petImageTag(href, x, y, w, h, 'data-kz-pet-image') + '</g>';
    }
    return '<g data-layer="pet">' + petImageTag(href, x, y, w, h, 'data-kz-pet-image') + '</g>';
  }
  /* Name (prominent) plus optional phone / message, bottom-anchored to the
   * original name baseline. Only non-empty lines render, so the preview mirrors
   * exactly what the customer types in the form. */
  var R_LINE_SPACING = 8;
  function textLines(root) {
    var state = stateOf(root);
    var name = state.name || 'KIZUNATAG';
    var lines = [{ t: name, size: 34, weight: 700, ls: 2 }];
    if (state.phone && String(state.phone).trim()) lines.push({ t: state.phone, size: 20, weight: 500, ls: 1 });
    if (state.message && String(state.message).trim()) lines.push({ t: state.message, size: 20, weight: 500, ls: 1 });
    var y = nameBaseline(plateGeom(root));
    for (var i = lines.length - 1; i >= 0; i--) { lines[i].y = y; if (i > 0) y -= (lines[i].size + R_LINE_SPACING); }
    lines.top = lines[0].y - lines[0].size;
    return lines;
  }
  function fitLengthAttr(root, l) {
    var maxW = plateGeom(root).w * 0.82;
    return (l.t.length * l.size > maxW)
      ? ' textLength="' + maxW.toFixed(0) + '" lengthAdjust="spacingAndGlyphs"' : '';
  }
  function textSvg(root) {
    var mat = material(root), ff = fontFamily(root), ink = mat.ink || '#2f2119';
    var inner = textLines(root).map(function (l) {
      return '<text x="250" y="' + l.y.toFixed(1) + '" text-anchor="middle" font-size="' + l.size + '" font-weight="' + l.weight + '" ' +
        'font-family=\'' + ff + '\' fill="' + ink + '" letter-spacing="' + l.ls + '"' + fitLengthAttr(root, l) +
        ' style="mix-blend-mode:' + (mat.blend || 'normal') + '" filter="url(#kr-carve)">' + esc(l.t) + '</text>';
    }).join('');
    return '<g data-layer="text">' + inner + '</g>';
  }
  /* Per-maker filter that turns the solid silhouette mask into a die-cut body:
   * a slightly larger material-coloured fill with an outer edge, so contour
   * products read like the real acrylic/wood cut to the pet outline. */
  function silFilter(root) {
    var mat = material(root), fill = matFill(root), edge = mat.edge || '#b98243';
    var op = mat.opacity == null ? 1 : mat.opacity;
    return '<filter id="kr-sil-' + stateOf(root).uid + '" x="-25%" y="-25%" width="150%" height="150%">' +
      '<feMorphology in="SourceAlpha" operator="dilate" radius="11" result="e"/>' +
      '<feFlood flood-color="' + edge + '"/><feComposite in2="e" operator="in" result="ef"/>' +
      '<feMorphology in="SourceAlpha" operator="dilate" radius="8" result="b"/>' +
      '<feFlood flood-color="' + fill + '" flood-opacity="' + op + '"/><feComposite in2="b" operator="in" result="bf"/>' +
      '<feMerge><feMergeNode in="ef"/><feMergeNode in="bf"/></feMerge></filter>';
  }
  function defs(root) {
    return '<linearGradient id="kr-metal" x1="0" x2="1"><stop offset="0" stop-color="#f8f8f6"/><stop offset=".25" stop-color="#b9b7b1"/><stop offset=".55" stop-color="#ffffff"/><stop offset="1" stop-color="#8d8a84"/></linearGradient>' +
      '<radialGradient id="kr-sheen" cx=".4" cy=".3" r=".9"><stop offset="0" stop-color="#ffffff" stop-opacity=".55"/><stop offset="1" stop-color="#ffffff" stop-opacity="0"/></radialGradient>' +
      '<filter id="kr-carve"><feDropShadow dx="0" dy="1.1" stdDeviation=".4" flood-color="#fff8ea" flood-opacity=".3"/><feDropShadow dx="0" dy="-1.1" stdDeviation=".4" flood-color="#1f140d" flood-opacity=".28"/></filter>' +
      '<filter id="kr-product-shadow"><feDropShadow dx="0" dy="12" stdDeviation="9" flood-color="#5f3d24" flood-opacity=".22"/></filter>' +
      (root && productIsContour(root) ? silFilter(root) : '');
  }
  function buildPreviewInner(root) {
    var sc = sizeScale(root);
    var inner = productBaseSvg(root) + materialTextureSvg(root) + cutGuideSvg(root) + petGroupSvg(root) +
      (productIsContour(root) ? '' : textSvg(root));
    return '<defs>' + defs(root) + '</defs>' +
      '<g filter="url(#kr-product-shadow)" transform="translate(250 250) scale(' + sc + ') translate(-250 -250)">' + inner + '</g>';
  }

  /* ---------- UI rendering ---------- */
  function tabList(root) {
    var sp = spec(root);
    var tabs = sp.tabs.map(function (cat) { return { key: cat, label: catLabel(root, cat, sp.labels[cat]) }; });
    tabs.push({ key: 'text', label: catLabel(root, 'text', '文字') });
    return tabs;
  }
  function selectedLabel(root, key) {
    var state = stateOf(root);
    if (key === 'text') return state.name || '';
    var n = state.sel[key];
    return n ? partLabel(root, key, n) : T('none', 'なし');
  }
  function render(root) {
    var tabs = tabList(root), state = stateOf(root);
    if (!tabs.some(function (t) { return t.key === state.active; })) state.active = tabs[0].key;
    renderTabs(root); renderPanel(root); renderParts(root); renderMethod(root);
    renderOptions(root); renderPreview(root); renderSummary(root); syncOrderData(root);
    renderCartButton(root); updatePet(root);
  }
  function renderCartButton(root) {
    var photo = stateOf(root).faceMode === 'photo';
    var label = photo ? T('cart_photo', '写真付きでカートに入れる') : T('cart', 'カートに入れる');
    $$(root, '[data-kz-cart]').forEach(function (el) {
      el.textContent = label;
      el.classList.toggle('kz-cart-photo', photo);
    });
  }
  function renderTabs(root) {
    var state = stateOf(root);
    var html = tabList(root).map(function (c) {
      return '<button class="kz-tab' + (c.key === state.active ? ' active' : '') + '" type="button" aria-pressed="' + (c.key === state.active ? 'true' : 'false') + '" aria-label="' + esc(c.label) + '" data-kz-tab="' + c.key + '">' +
        '<span class="kz-tab-mark ' + c.key + '" aria-hidden="true"></span>' + esc(c.label) + '</button>';
    }).join('');
    $$(root, '[data-kz-tabs]').forEach(function (el) { el.innerHTML = html; });
  }
  function renderPanel(root) {
    var state = stateOf(root);
    var jaLab = state.active === 'text' ? '文字' : spec(root).labels[state.active];
    var lab = catLabel(root, state.active, jaLab);
    var titleJa = jaLab + 'を選ぶ';
    var title = (i18() && !i18().isJa) ? i18().fmt(T('choose_fmt', '%sを選ぶ'), lab) : titleJa;
    $$(root, '[data-kz-panel-title]').forEach(function (el) { el.textContent = title; });
    $$(root, '[data-kz-selected-label]').forEach(function (el) { el.textContent = selectedLabel(root, state.active); });
  }
  /* Trim each part to its ink bounding box and center it on a square canvas so
   * thumbnails line up regardless of where the drawing sits in the source JPG. */
  var thumbCache = new Map();
  function makeThumb(root, cat, n) {
    var key = species(root) + '|' + cat + '|' + n;
    if (thumbCache.has(key)) return thumbCache.get(key);
    var p = processImage(partUrl(root, cat, n), spec(root).alpha, [47, 33, 25]).then(function (r) {
      if (!r.src) return '';
      return new Promise(function (res) {
        var im = new Image();
        im.onload = function () {
          var iw = im.naturalWidth, ih = im.naturalHeight;
          var cw = Math.max(1, Math.round(r.bw * iw));
          var ch = Math.max(1, Math.round(r.bw * iw * r.car));
          var sx = r.ccx * iw - cw / 2, sy = r.ccy * ih - ch / 2;
          var S = 220, pad = 24, avail = S - 2 * pad;
          var scale = Math.min(avail / cw, avail / ch);
          var dw = cw * scale, dh = ch * scale;
          var c = document.createElement('canvas'); c.width = S; c.height = S;
          var x = c.getContext('2d');
          x.imageSmoothingEnabled = true; x.imageSmoothingQuality = 'high';
          x.drawImage(im, sx, sy, cw, ch, (S - dw) / 2, (S - dh) / 2, dw, dh);
          res(c.toDataURL('image/png'));
        };
        im.onerror = function () { res(''); };
        im.src = r.src;
      });
    });
    thumbCache.set(key, p);
    return p;
  }
  function partThumb(root, cat, n) {
    if (!n) return '<span class="kz-thumb-img kz-thumb-none">' + esc(T('none', 'なし')) + '</span>';
    return '<span class="kz-thumb-img" aria-hidden="true" data-kz-thumb="' + cat + ':' + n + '"></span>';
  }
  function hydrateThumbs(root) {
    $$(root, '[data-kz-thumb]').forEach(function (el) {
      var parts = el.getAttribute('data-kz-thumb').split(':');
      makeThumb(root, parts[0], Number(parts[1])).then(function (url) {
        if (url) el.style.backgroundImage = "url('" + url + "')";
      });
    });
  }
  function choiceButton(cat, n, art, name, active) {
    var lbl = '<span class="kz-part-no">#' + n + '</span>' +
      (name ? '<span class="kz-part-name">' + esc(name) + '</span>' : '');
    return '<button class="kz-part' + (active ? ' active' : '') + '" type="button" aria-pressed="' + (active ? 'true' : 'false') + '" aria-label="' + esc(name || ('#' + n)) + '" data-kz-select data-cat="' + cat + '" data-id="' + n + '">' +
      art + '<span class="kz-part-label">' + lbl + '</span></button>';
  }
  function renderParts(root) {
    var state = stateOf(root), key = state.active, html;
    var photoActive = state.faceMode === 'photo';
    if (key === 'text') {
      html = '<div class="kz-part-note">' + esc(T('note_text', '下のフォームに、お名前・電話番号・メッセージを入力してください。入力するとプレビューにすぐ反映されます。')) + '</div>';
    } else if (key === 'pattern' && faceHasOwnPattern(root)) {
      html = '<div class="kz-part-note">' + esc(T('note_selfpattern', '選択中のお顔はもともとブチ柄が入っているため、追加の模様はつきません。別の模様をつけたい場合は、無地のお顔をお選びください。')) + '</div>';
    } else {
      var sp = spec(root), nums = sp.nums[key] || [];
      var presetActive = key === 'face' ? (photoActive ? false : !state.sel[key]) : !state.sel[key];
      var noneLab = esc(T('none', 'なし'));
      html = '<button class="kz-part' + (presetActive ? ' active' : '') + '" type="button" aria-pressed="' + (presetActive ? 'true' : 'false') + '" aria-label="' + noneLab + '" data-kz-select data-cat="' + key + '" data-id="0">' +
        '<span class="kz-thumb-img kz-thumb-none">' + noneLab + '</span><span>' + noneLab + '</span></button>';
      html += nums.map(function (n) {
        var active = (key === 'face' && photoActive) ? false : state.sel[key] === n;
        return choiceButton(key, n, partThumb(root, key, n), partName(root, key, n), active);
      }).join('');
      if (key === 'face' && photoActive) {
        html = '<div class="kz-part-note">' + esc(T('note_photo_active', '現在「お写真から刻印デザイン作成」を選択中です。下の「作成方法を選ぶ」で通常作成に戻すか、パーツを選ぶと自動で通常作成に切り替わります。')) + '</div>' + html;
      }
    }
    $$(root, '[data-kz-parts]').forEach(function (el) { el.innerHTML = html; });
    if (key !== 'text') hydrateThumbs(root);
  }
  /* 作成方法を選ぶ：通常作成 / お写真から刻印デザイン作成（カード式） */
  function renderMethod(root) {
    var state = stateOf(root), photoActive = state.faceMode === 'photo';
    var fee0 = photoFee(root), feeR = photoRegularFee(root), promo = photoPromo(root);
    var feeHtml = promo
      ? '<span class="kz-method-fee"><span class="kz-method-fee-old">' + esc(tf('fee_regular_fmt', '通常＋%s円のところ', feeR.toLocaleString())) + '</span><span class="kz-method-fee-now">' + esc(tf('fee_now_fmt', '今だけ ＋%s円', fee0.toLocaleString())) + '</span></span>'
      : '<span class="kz-method-fee"><span class="kz-method-fee-now">' + esc(tf('fee_plain_fmt', '＋%s円', fee0.toLocaleString())) + '</span></span>';
    var normal = '<button class="kz-method-card' + (photoActive ? '' : ' active') + '" type="button" aria-pressed="' + (photoActive ? 'false' : 'true') + '" data-kz-face-mode="preset">' +
      '<span class="kz-method-radio" aria-hidden="true"></span>' +
      '<span class="kz-method-body"><span class="kz-method-name">' + esc(T('method_normal_name', '通常作成')) + '</span>' +
      '<span class="kz-method-desc">' + esc(T('method_normal_desc', 'パーツを選んで、自分でデザインを作成します。')) + '</span>' +
      '<span class="kz-method-fee"><span class="kz-method-fee-now kz-method-fee-zero">' + esc(T('fee_zero', '追加料金なし')) + '</span></span></span></button>';
    var photo = '<button class="kz-method-card kz-method-card-photo' + (photoActive ? ' active' : '') + '" type="button" aria-pressed="' + (photoActive ? 'true' : 'false') +
      '" aria-label="' + esc(T('method_photo_name', 'お写真から刻印デザイン作成')) + '" data-kz-face-mode="photo">' +
      '<span class="kz-method-badge">' + esc(T('badge_popular', '人気')) + '</span>' +
      '<span class="kz-method-radio" aria-hidden="true"></span>' +
      '<span class="kz-method-body"><span class="kz-method-name">' + esc(T('method_photo_name', 'お写真から刻印デザイン作成')) + '</span>' +
      '<span class="kz-method-desc">' + esc(T('method_photo_desc', 'お預かりした写真をもとに、職人が刻印用の線画デザインへ調整します。')) + '</span>' +
      feeHtml + '</span></button>';
    var html = normal + photo + (photoActive ? photoPanel(root) : '');
    $$(root, '[data-kz-method]').forEach(function (el) { el.innerHTML = html; });
  }
  function photoPanel(root) {
    var state = stateOf(root);
    var uid = state.uid, hasFile = !!state.photoFile;
    var notesId = 'kz-photo-notes-' + uid, inputId = 'kz-photo-input-' + uid;
    var fee0 = photoFee(root), feeR = photoRegularFee(root), promo = photoPromo(root);
    var feeTag = promo
      ? '<span class="kz-photo-fee-tag"><span class="kz-photo-fee-old">' + esc(tf('fee_regular_fmt', '通常＋%s円のところ', feeR.toLocaleString())) + '</span><span class="kz-photo-fee-now">' + esc(tf('fee_now_fmt', '今だけ ＋%s円', fee0.toLocaleString())) + '</span></span>'
      : '<span class="kz-photo-fee-tag"><span class="kz-photo-fee-now">' + esc(tf('fee_plain_fmt', '＋%s円', fee0.toLocaleString())) + '</span></span>';
    var promoBadge = promo ? '<span class="kz-photo-promo">' + esc(T('promo_label', 'オープン記念価格')) + '</span>' : '';
    /* STEP 1 upload status */
    var uploadStatus = hasFile
      ? '<div class="kz-photo-preview"><img src="' + esc(state.photoPreviewUrl) + '" alt="' + esc(T('photo_uploaded', '✓ アップロード済み')) + '">' +
        '<div class="kz-photo-meta"><span class="kz-photo-status">' + esc(T('photo_uploaded', '✓ アップロード済み')) + '</span><span class="kz-photo-name">' + esc(state.photoFile.name) + '</span>' +
        '<p class="kz-photo-sent-note">' + esc(T('photo_sent_note', 'アップロード後は、必要事項を入力して「写真付きでカートに入れる」へお進みください。この写真はご注文内容と一緒に送信されます。')) + '</p>' +
        '<div class="kz-photo-btns"><button type="button" class="kz-photo-btn" data-kz-photo-change>' + esc(T('photo_change', '写真を変更する')) + '</button><button type="button" class="kz-photo-btn kz-photo-btn-del" data-kz-photo-remove>' + esc(T('photo_remove', '写真を削除する')) + '</button></div></div></div>'
      : '';
    var err = state.photoError
      ? '<p class="kz-photo-err" role="alert">' + esc(state.photoError) + '</p>'
      : '<p class="kz-photo-err" role="alert" aria-live="polite"></p>';
    var step1 = '<div class="kz-photo-step">' +
      '<p class="kz-photo-step-title"><span class="kz-step-num">STEP 1</span>' + esc(T('step1_title', '写真をアップロード')) + '</p>' +
      '<input id="' + inputId + '" class="kz-photo-input" type="file" accept="image/jpeg,image/png,image/webp" data-kz-photo-input aria-describedby="' + notesId + '">' +
      '<label class="kz-photo-upload-btn" for="' + inputId + '">' + esc(hasFile ? T('upload_reselect', '別の写真を選び直す') : T('upload_select', '写真を選択する')) + '</label>' +
      '<p class="kz-photo-help">' + esc(T('upload_help', 'JPG / PNG / WEBP対応・10MB以内。お顔がはっきり大きく写っている写真がおすすめです。')) + '</p>' +
      uploadStatus + err + '</div>';
    var step2 = '<div class="kz-photo-step">' +
      '<p class="kz-photo-step-title"><span class="kz-step-num">STEP 2</span>' + esc(T('step2_title', '注意事項に同意')) + '</p>' +
      '<div class="kz-photo-notes" id="' + notesId + '">' +
        '<div class="kz-photo-note-cols"><div class="kz-photo-note-ok"><strong>' + esc(T('rec_title', 'おすすめ写真')) + '</strong><ul><li>' + esc(T('rec_1', '正面または横顔がはっきり写っている')) + '</li><li>' + esc(T('rec_2', 'お顔が大きく写っている')) + '</li><li>' + esc(T('rec_3', '明るく、ピントが合っている')) + '</li><li>' + esc(T('rec_4', '背景ができるだけシンプル')) + '</li></ul></div>' +
        '<div class="kz-photo-note-ng"><strong>' + esc(T('bad_title', '不向きな写真')) + '</strong><ul><li>' + esc(T('bad_1', '暗い写真')) + '</li><li>' + esc(T('bad_2', 'ピントが合っていない写真')) + '</li><li>' + esc(T('bad_3', 'お顔が小さい写真')) + '</li><li>' + esc(T('bad_4', '背景や装飾が多すぎる写真')) + '</li></ul></div></div>' +
        '<p class="kz-photo-caution">' + T('photo_caution', '※お写真をもとに線画化して制作するため、実際の刻印は写真と完全に同一にはなりません。<br>※レーザー刻印の特性上、素材により線の太さ・濃淡・表情の見え方に若干の差が生じる場合があります。<br>※細かすぎる模様や背景は、刻印に適した形へ調整する場合があります。').replace(/\n/g, '<br>') + '</p>' +
      '</div>' +
      '<label class="kz-photo-agree' + (state.accepted ? ' is-checked' : '') + '"><input type="checkbox" data-kz-accept' + (state.accepted ? ' checked' : '') + ' aria-describedby="' + notesId + '"><span>' + esc(T('agree_text', '写真をもとに線画デザインを作成するため、実際の刻印が写真と完全に同一にはならないことを了承しました。')) + '</span></label>' +
      '</div>';
    var step3 = '<div class="kz-photo-step kz-photo-step-submit">' +
      '<p class="kz-photo-step-title"><span class="kz-step-num">STEP 3</span>' + esc(T('step3_title', 'ご注文内容を確認してカートに入れる')) + '</p>' +
      '<p class="kz-photo-submit-note">' + esc(T('submit_note', 'お名前・電話番号・メッセージ・アップロード写真をご確認のうえ、問題がなければ下の「写真付きでカートに入れる」ボタンを押してください。アップロードした写真は、ご注文内容と一緒に送信されます。')) + '</p>' +
      '</div>';
    return '<div class="kz-photo-option-panel">' +
      '<div class="kz-photo-option-head"><strong>' + esc(T('method_photo_name', 'お写真から刻印デザイン作成')) + '</strong>' + feeTag + '</div>' +
      promoBadge +
      '<p class="kz-photo-lead">' + esc(T('photo_lead', 'お預かりしたペットのお写真をもとに、レーザー刻印に適した線画デザインへ職人が丁寧に調整して制作します。写真をそのまま印刷・転写するものではなく、素材やサイズに合わせて線の太さや細部を調整します。')) + '</p>' +
      step1 + step2 + step3 +
      '</div>';
  }
  function renderOptions(root) {
    var state = stateOf(root), C = catalog();
    if (C.normalize) C.normalize(state);
    fillOptions(root, 'product', DATA.products || [], state.product, productThumb);
    fillOptions(root, 'material', C.materialsFor ? C.materialsFor(state.product) : (DATA.materials || []), state.material, materialThumb);
    fillOptions(root, 'size', C.sizesFor ? C.sizesFor(state.product) : (DATA.sizes || []), state.size, sizeThumb, sizeChoiceLabel);
  }
  function fillOptions(root, cat, items, current, art, labelFn) {
    var I18 = i18();
    var coll = cat === 'product' ? 'products' : (cat === 'material' ? 'materials' : (cat === 'font' ? 'fonts' : null));
    var html = items.map(function (item, i) {
      var ja = item.label || item.name || '';
      var locLab = (I18 && coll) ? I18.label(coll, item.id, ja) : ja;
      var label = labelFn ? labelFn(item) : '<span>' + esc(locLab) + '</span>';
      return '<button class="kz-choice' + (item.id === current ? ' active' : '') + '" type="button" aria-pressed="' + (item.id === current ? 'true' : 'false') + '" aria-label="' + esc(locLab) + '" data-kz-select data-cat="' + cat + '" data-id="' + esc(item.id) + '">' +
        art(item, i, items.length) + label + '</button>';
    }).join('');
    $$(root, '[data-kz-options="' + cat + '"]').forEach(function (el) { el.innerHTML = html; });
  }
  function sizeChoiceLabel(item) {
    var I18 = i18();
    var nm = I18 ? I18.size(item.name) : item.name;
    var mm = I18 ? I18.size(item.mm) : item.mm;
    return '<span class="kz-size-label"><span class="kz-size-name">' + esc(nm) + '</span>' +
      '<span class="kz-size-mm">' + esc(mm) + '</span></span>';
  }
  /* Per-product icon silhouettes (viewBox 0 0 80 80) so each card visibly looks
   * like the real item, not a generic rounded tag. */
  function shapeIconSvg(key) {
    var F = 'fill="#f0d9b0" stroke="#c69a67" stroke-width="3"';
    var L = 'fill="none" stroke="#c69a67" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"';
    var M = 'fill="none" stroke="#b9b39f" stroke-width="2.4"';
    var hole = function (cx, cy) { return '<circle cx="' + cx + '" cy="' + cy + '" r="3.4" fill="#fff" stroke="#c69a67" stroke-width="2"/>'; };
    switch (key) {
      case 'tag':
        return '<circle cx="40" cy="14" r="7" ' + M + '/><circle cx="40" cy="46" r="22" ' + F + '/>' + hole(40, 28);
      case 'keyholder':
        return '<circle cx="40" cy="13" r="6" ' + M + '/><path d="M21 34 Q23 17 30 14 Q34 22 36 31 Z" ' + F + '/><path d="M59 34 Q57 17 50 14 Q46 22 44 31 Z" ' + F + '/><ellipse cx="40" cy="47" rx="20" ry="18" ' + F + '/>';
      case 'charm':
        return '<circle cx="40" cy="9" r="4" ' + M + '/><circle cx="40" cy="16" r="2.2" fill="#cfcdc8"/><path d="M22 37 Q24 21 31 18 Q35 26 37 34 Z" ' + F + '/><path d="M58 37 Q56 21 49 18 Q45 26 43 34 Z" ' + F + '/><ellipse cx="40" cy="49" rx="19" ry="17" ' + F + '/>';
      case 'plate':
        return '<rect x="10" y="28" width="60" height="24" rx="8" ' + F + '/>';
      case 'light':
        return '<ellipse cx="40" cy="32" rx="27" ry="20" ' + F + '/><path d="M31 56 L34 50 H46 L49 56 Z" ' + F + '/><path d="M26 58 H54" ' + L + '/>';
      case 'roomsign':
        return '<path d="M24 30 L40 16 L56 30" ' + L + '/><rect x="16" y="30" width="48" height="28" rx="7" ' + F + '/>';
      case 'memorial':
        return '<rect x="22" y="12" width="36" height="40" rx="5" ' + F + '/><path d="M30 58 L33 52 H47 L50 58 Z" ' + F + '/><path d="M25 60 H55" ' + L + '/>';
      case 'ornament':
        return hole(40, 13) + '<circle cx="40" cy="46" r="22" ' + F + '/>';
      case 'leathertag':
        return hole(40, 13) + '<circle cx="40" cy="44" r="22" ' + F + '/><circle cx="40" cy="44" r="16" fill="none" stroke="#fff" stroke-width="1.6" stroke-dasharray="1.5 4"/>';
      case 'coaster':
        return '<circle cx="40" cy="40" r="26" ' + F + '/><circle cx="40" cy="40" r="18" fill="none" stroke="#c69a67" stroke-width="2" stroke-dasharray="3 5"/>';
      case 'doorplate':
        return '<path d="M30 36 L40 13 L50 36" ' + L + '/><ellipse cx="40" cy="46" rx="30" ry="17" ' + F + '/>' + hole(30, 38) + hole(50, 38);
      case 'nameplate':
        return '<path d="M20 30 Q22 12 30 9 Q34 18 37 28 Z" ' + F + '/><path d="M60 30 Q58 12 50 9 Q46 18 43 28 Z" ' + F + '/><ellipse cx="40" cy="43" rx="21" ry="18" ' + F + '/><path d="M26 62 L29 55 H51 L54 62 Z" ' + F + '/>';
      default:
        return '<rect x="26" y="14" width="28" height="52" rx="11" ' + F + '/>';
    }
  }
  function productThumb(prod) {
    return '<span class="kz-choice-art" aria-hidden="true"><svg class="kz-choice-svg" viewBox="0 0 80 80" aria-hidden="true" focusable="false">' +
      shapeIconSvg(shapeKeyOf(prod)) + '</svg></span>';
  }
  function materialThumb(mat) { return '<span class="kz-material-swatch" aria-hidden="true" style="background:' + (mat.swatch || '#e6bd82') + '"></span>'; }
  function sizeThumb(i, total) {
    var n = total || 3;
    var rs = n <= 1 ? [18] : [12, 16, 21];
    var r = rs[Math.min(i, rs.length - 1)] || 16;
    return '<span class="kz-choice-art" aria-hidden="true"><svg class="kz-choice-svg" viewBox="0 0 80 64" aria-hidden="true" focusable="false"><circle cx="40" cy="32" r="' + r + '" fill="#f5dfb8" stroke="#c69a67" stroke-width="3"/></svg></span>';
  }
  function renderPreview(root) {
    var svg = buildPreviewInner(root), uid = stateOf(root).uid;
    // Several makers can coexist (homepage). Make every defs id globally unique
    // per maker instance + preview, otherwise url(#..) refs can resolve into a
    // hidden (display:none) maker's defs and the filtered group never paints.
    $$(root, '[data-kz-preview]').forEach(function (el, i) {
      el.innerHTML = svg.replace(/kr-(metal|sheen|carve|product-shadow)/g, 'kr-$1-' + uid + '-' + i);
    });
  }
  function renderSummary(root) {
    var state = stateOf(root);
    var rows = [
      [T('row_face', 'お顔'), selectedLabel(root, 'face')],
      [T('row_mouth', 'お口元'), selectedLabel(root, 'muzzle')],
      [T('row_product', '商品'), prodLabelUi(root, (findById('products', state.product) || {}).label)],
      [T('row_material', '素材'), matLabelUi(root, (findById('materials', state.material) || {}).label)],
      [T('row_size', 'サイズ'), sizeSummaryUi(root)],
      [T('row_name', 'お名前'), state.name || '']
    ];
    var html = '<b>' + esc(T('order_summary', 'ご注文内容')) + '</b><br>' + rows.map(function (r) { return esc(r[0]) + '：' + esc(r[1]); }).join('<br>') +
      '<small>' + esc(T('summary_note', '実際の刻印では、素材に合わせて線の太さや濃淡を調整して制作します。')) + '</small>';
    $$(root, '[data-kz-summary]').forEach(function (el) { el.innerHTML = html; });
    var prefix = root.dataset.productPrefix || species(root);
    var products = DATA.products || [], idx = 0;
    for (var i = 0; i < products.length; i++) { if (products[i].id === state.product) { idx = i; break; } }
    $$(root, '[data-kz-finish-image]').forEach(function (img) { img.src = assetUrl(prefix + '-product-' + (idx + 1) + '.jpg'); });
  }

  /* recompose the pet PNG asynchronously and patch the preview <image> */
  function updatePet(root) {
    var state = stateOf(root);
    if (state.faceMode === 'photo') return; /* photo mode shows a placeholder, no compose */
    var token = ++state.petToken;
    composeCanvas(root, 8).then(function (canvas) {
      if (token !== state.petToken) return; /* a newer change superseded this */
      if (productIsContour(root)) {
        var sil = silhouetteFrom(canvas);
        if (sil) {
          state.petPng = cropDataUrl(canvas, sil.bb);
          state.petSilPng = cropDataUrl(sil.canvas, sil.bb);
          state.petAR = sil.bb.h / sil.bb.w;
        } else {
          var tc = trimCanvas(canvas);
          state.petPng = tc.url; state.petSilPng = ''; state.petAR = tc.ar;
        }
      } else {
        var t = trimCanvas(canvas);
        state.petPng = t.url; state.petSilPng = ''; state.petAR = t.ar;
      }
      /* re-render so the <image> picks up the new size/position for this aspect ratio */
      renderPreview(root);
      syncOrderData(root);
    });
  }

  /* ---------- order data ---------- */
  function designData(root) {
    var state = stateOf(root), photo = state.faceMode === 'photo';
    return {
      version: VERSION, species: species(root), engine: 'raster',
      parts: state.sel,
      product: state.product, material: state.material, size: state.size, font: state.font,
      text: { name: state.name, phone: state.phone, message: state.message },
      faceMode: state.faceMode,
      photoOptionEnabled: photo,
      photoOptionFee: photoFee(root),
      photoOptionRegularFee: photoRegularFee(root),
      photoOptionPromo: photoPromo(root),
      uploadedPhotoName: photo && state.photoFile ? state.photoFile.name : null,
      engravingDifferenceAccepted: photo ? !!state.accepted : false
    };
  }
  /* 生産担当者向けの短いデザイン要約（例: 猫 / 写真作成 / 目:まんまる / 口:ふんわり / ひげなし / 飾りなし）。 */
  function buildDesignSummary(root) {
    var st = stateOf(root), photo = st.faceMode === 'photo';
    function nm(cat) { var n = st.sel[cat]; if (!n) return ''; return partNameJa(root, cat, n) || ('#' + n); }
    var parts = [];
    parts.push(species(root) === 'dog' ? '犬' : '猫');
    parts.push(photo ? '写真作成' : 'イラスト作成');
    if (!photo) { var f = nm('face'); if (f) parts.push('顔:' + f); }
    var e = nm('eyes'); parts.push(e ? ('目:' + e) : '目:指定なし');
    var m = nm('muzzle'); parts.push(m ? ('口:' + m) : '口:指定なし');
    parts.push(st.sel.whiskers ? ('ひげ:' + nm('whiskers')) : 'ひげなし');
    parts.push(st.sel.decoration ? ('飾り:' + nm('decoration')) : '飾りなし');
    return parts.join(' / ');
  }
  function orderProperties(root) {
    var state = stateOf(root), sp = spec(root), photo = state.faceMode === 'photo';
    /* 制作番号 を先頭に（生産担当が最初に見る）。長い JSON(_design_data) は末尾に固定。 */
    var props = { '制作番号': '', 'うちの子': species(root) === 'dog' ? 'わんちゃん' : '猫ちゃん' };
    sp.tabs.forEach(function (cat) { props[sp.labels[cat]] = selectedLabelJa(root, cat); });
    if (photo) {
      props['お顔'] = 'お写真から刻印デザイン作成';
      props['写真作成オプション'] = 'あり（＋' + photoFee(root).toLocaleString() + '円）';
      props['刻印仕上がり差異への同意'] = '同意済み';
    } else {
      props['写真作成オプション'] = 'なし';
    }
    props['商品'] = (findById('products', state.product) || {}).label || '';
    props['素材'] = (findById('materials', state.material) || {}).label || '';
    props['サイズ'] = sizeSummaryLabel(root);
    props['お名前'] = state.name || ''; props['電話番号'] = state.phone || ''; props['メッセージ'] = state.message || '';
    props['デザイン要約'] = buildDesignSummary(root);
    /* 画像 URL は addToCart の外部アップロード後に埋める（未設定なら空文字のまま）。 */
    props['仕上がりイメージURL'] = '';
    props['ペット写真URL'] = '';
    props['_design_data'] = JSON.stringify(designData(root));
    return props;
  }
  function syncOrderData(root) { root.dataset.kzOrderProperties = JSON.stringify(orderProperties(root)); }

  /* ---------- production id / preview export / external upload ---------- */
  function pad2(n) { return n < 10 ? '0' + n : '' + n; }
  function generateProductionId() {
    var d = new Date();
    var ymd = '' + d.getFullYear() + pad2(d.getMonth() + 1) + pad2(d.getDate());
    var hms = pad2(d.getHours()) + pad2(d.getMinutes()) + pad2(d.getSeconds());
    var chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789', rnd = '';
    for (var i = 0; i < 4; i++) rnd += chars.charAt(Math.floor(Math.random() * chars.length));
    return 'KZT-' + ymd + '-' + hms + '-' + rnd;
  }
  function getOriginalPhotoFile(root) { var st = stateOf(root); return (st && st.photoFile) || null; }
  function uploadEndpoint(root) { return (root && root.dataset.uploadEndpoint) || window.KIZUNATAG_UPLOAD_ENDPOINT || ''; }
  function previewSvgEl(root) {
    var els = $$(root, '[data-kz-preview]');
    for (var i = 0; i < els.length; i++) { try { if (els[i].getBoundingClientRect().width > 0) return els[i]; } catch (e) { } }
    return els[0] || null;
  }
  /* 商品ページの仕上がりプレビュー SVG を 1200x1200 の PNG Blob にラスタライズする。
   * SVG 内のペット線画は dataURL 埋め込みのため canvas が汚染されず toBlob 可能。
   * 写真モードはプレースホルダーがそのまま書き出される（生産は原画像を参照）。 */
  async function generatePreviewBlob(root) {
    var svg = previewSvgEl(root);
    if (!svg) throw new Error('preview svg not found');
    var clone = svg.cloneNode(true);
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    clone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
    clone.setAttribute('width', '1200');
    clone.setAttribute('height', '1200');
    if (!clone.getAttribute('viewBox')) clone.setAttribute('viewBox', '0 0 500 500');
    var xml = new XMLSerializer().serializeToString(clone);
    var src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(xml);
    var img = await new Promise(function (res, rej) {
      var im = new Image();
      im.onload = function () { res(im); };
      im.onerror = function () { rej(new Error('preview svg render failed')); };
      im.src = src;
    });
    var c = document.createElement('canvas'); c.width = 1200; c.height = 1200;
    var ctx = c.getContext('2d');
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, 1200, 1200);
    ctx.drawImage(img, 0, 0, 1200, 1200);
    return await new Promise(function (res, rej) {
      c.toBlob(function (b) { b ? res(b) : rej(new Error('preview toBlob failed')); }, 'image/png');
    });
  }
  /* 外部ストレージ（R2/S3/Supabase 等）へ原画像と仕上がり画像をアップロードし、
   * 公開 URL を返す。エンドポイント未設定なら空 URL を返してスキップ（現状維持）。 */
  async function uploadProductionImages(root, opts) {
    var ep = uploadEndpoint(root);
    if (!ep) return { original_photo_url: '', preview_image_url: '' };
    var fd = new FormData();
    fd.append('production_id', opts.productionId);
    if (opts.originalPhotoFile) fd.append('original_photo', opts.originalPhotoFile, opts.originalPhotoFile.name);
    fd.append('preview_image', opts.previewBlob, opts.productionId + '-preview.png');
    var res = await fetch(ep, { method: 'POST', body: fd });
    if (!res.ok) throw new Error('upload http ' + res.status);
    var json = await res.json();
    if (!json || json.ok !== true) throw new Error((json && json.error) || 'upload failed');
    return { original_photo_url: json.original_photo_url || '', preview_image_url: json.preview_image_url || '' };
  }

  async function addToCart(root) {
    var state = stateOf(root), photo = state.faceMode === 'photo';
    if (!state.name || !state.name.trim()) {
      alert(T('alert_name', 'お名前を入力してください'));
      if (state.active !== 'text') dispatch(root, { type: 'tab', key: 'text' });
      var nameInput = root.querySelector('[data-kz-input="name"]');
      if (nameInput) nameInput.focus();
      return;
    }
    if (photo) {
      if (!state.photoFile) {
        alert(T('alert_photo_required', '写真から作成を選択した場合は、ペット写真をアップロードしてください。'));
        if (state.active !== 'face') dispatch(root, { type: 'tab', key: 'face' });
        return;
      }
      if (!state.accepted) {
        alert(T('alert_agree', '刻印仕上がりの差異について同意してください。'));
        if (state.active !== 'face') dispatch(root, { type: 'tab', key: 'face' });
        return;
      }
    }
    // Guard against double submit; show progress on the cart button(s).
    if (root._kzSubmitting) return;
    root._kzSubmitting = true;
    var carts = $$(root, '[data-kz-cart]');
    var prevLabels = carts.map(function (b) { return b.textContent; });
    carts.forEach(function (b) { b.disabled = true; b.classList.add('is-uploading'); b.textContent = T('uploading', 'アップロード中...'); });
    var uploadFailed = false;
    try {
      // Resolve the variant for the product currently chosen in the generator by
      // matching its label against the store product titles (data-product-map).
      // Falls back to the single product configured via section settings so the
      // section still works without a collection mapping.
      var match = matchVariant(root);
      var fbNormal = root.dataset.variantId;
      var fbPhoto = root.dataset.photoVariantId || root.dataset.variantId;
      var id = match
        ? (photo ? (match.photoVariant || match.variant) : match.variant)
        : (photo ? fbPhoto : fbNormal);
      if (!id) { alert(T('alert_unavailable', 'ただいま商品の準備中です。お手数ですが、少し時間をおいてもう一度お試しください。')); return; }
      var props = orderProperties(root);
      var productionId = generateProductionId();
      props['制作番号'] = productionId;
      var originalPhotoFile = getOriginalPhotoFile(root);

      // Build the 1200px finish preview. It is attached as a Shopify file
      // property below (so the order always carries a clickable big-image URL
      // with NO external backend), and also used for external upload if set.
      var previewBlob = null;
      try { previewBlob = await generatePreviewBlob(root); }
      catch (pe) { if (uploadEndpoint(root)) { uploadFailed = true; throw pe; } }

      // Optional external storage (R2/S3/...). Only when an endpoint is set.
      // On failure we block the add so production data is never lost.
      if (uploadEndpoint(root)) {
        try {
          var urls = await uploadProductionImages(root, { productionId: productionId, originalPhotoFile: originalPhotoFile, previewBlob: previewBlob });
          props['仕上がりイメージURL'] = urls.preview_image_url || '';
          props['ペット写真URL'] = urls.original_photo_url || '';
        } catch (upErr) { uploadFailed = true; throw upErr; }
      }
      var fd = new FormData();
      fd.append('id', String(Number(id)));
      fd.append('quantity', '1');
      Object.keys(props).forEach(function (k) { fd.append('properties[' + k + ']', props[k]); });
      // 仕上がりイメージ: 1200px finish preview as a Shopify-hosted file. The
      // order property value becomes a public URL (used by Flow -> Sheets).
      if (previewBlob) fd.append('properties[仕上がりイメージ]', previewBlob, productionId + '-preview.png');
      // Attach production-ready files so the merchant can engrave/cut directly:
      //  - 加工用データ: high-res pure-black line art (+ name text for standard shapes)
      //  - カットライン: solid pet silhouette for contour products
      var spc = species(root);
      if (!photo) {
        try {
          var engBlob = dataURLToBlob(await composeEngravingPng(root));
          if (engBlob) fd.append('properties[加工用データ]', engBlob, 'kizunatag-' + spc + '-engraving.png');
        } catch (e) { }
        try {
          var cutData = await composeCutPng(root);
          if (cutData) {
            var cutBlob = dataURLToBlob(cutData);
            if (cutBlob) fd.append('properties[カットライン]', cutBlob, 'kizunatag-' + spc + '-cut.png');
          }
        } catch (e2) { }
      }
      // ペット写真: customer's original upload as a Shopify-hosted file (URL via Flow).
      if (state.photoFile) fd.append('properties[ペット写真]', state.photoFile, state.photoFile.name);
      var res = await fetch('/cart/add.js', { method: 'POST', body: fd });
      if (!res.ok) throw new Error('cart add failed');
      window.location.href = '/cart';
    } catch (e) {
      alert(uploadFailed
        ? T('alert_upload_failed', '画像アップロードに失敗しました。もう一度お試しください。')
        : T('alert_cart_failed', 'カートに追加できませんでした。入力内容をご確認のうえ、もう一度お試しください。'));
    } finally {
      carts.forEach(function (b, i) { b.disabled = false; b.classList.remove('is-uploading'); b.textContent = prevLabels[i]; });
      root._kzSubmitting = false;
    }
  }
  function tryAnchorDownload(href, filename) {
    try {
      var a = document.createElement('a');
      a.href = href; a.download = filename; a.rel = 'noopener';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return true;
    } catch (e) { return false; }
  }
  /* Save the finish preview the customer sees (same 1200px image attached to the
   * order). Some browsers (in-app webviews, iframes, strict download settings)
   * silently block programmatic downloads, so we ALSO open the image in a new
   * tab — the user always sees it and can long-press / right-click to save. */
  async function downloadPng(root, btn) {
    var prev = btn ? btn.textContent : '';
    if (btn) { btn.disabled = true; btn.textContent = T('saving', '保存中...'); }
    try {
      var name = 'kizunatag-' + species(root) + '-' + generateProductionId() + '.png';
      var url = '', isBlob = false;
      try {
        var blob = await generatePreviewBlob(root);
        if (blob && window.URL && URL.createObjectURL) { url = URL.createObjectURL(blob); isBlob = true; }
      } catch (e) { }
      if (!url) url = await composePng(root, 8); // data URL fallback (line art)

      tryAnchorDownload(url, name); // best-effort real file download
      var win = null;
      try { win = window.open(url, '_blank'); } catch (e) { }
      if (!win) {
        // Popup blocked: open a preview overlay in-page so the image is visible.
        showImagePreview(root, url, name);
      }
      if (isBlob) setTimeout(function () { try { URL.revokeObjectURL(url); } catch (e) { } }, 60000);
      if (btn) btn.textContent = win ? T('opened_tab', '別タブで画像を開きました') : T('downloaded', '画像を表示しました');
    } catch (err) {
      if (btn) btn.textContent = T('save_failed', '保存できませんでした');
      else alert(T('save_failed', '保存できませんでした'));
    } finally {
      if (btn) { btn.disabled = false; setTimeout(function () { btn.textContent = prev; }, 1800); }
    }
  }
  /* In-page fallback when both download and popup are blocked: show the image in
   * an overlay with a tappable save link the user can act on directly. */
  function showImagePreview(root, url, name) {
    var ov = document.createElement('div');
    ov.setAttribute('data-kz-img-overlay', '');
    ov.style.cssText = 'position:fixed;inset:0;z-index:99999;background:rgba(0,0,0,.8);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:16px;padding:20px;';
    var img = document.createElement('img');
    img.src = url; img.alt = name;
    img.style.cssText = 'max-width:90vw;max-height:75vh;background:#fff;border-radius:8px;box-shadow:0 8px 30px rgba(0,0,0,.4);';
    var link = document.createElement('a');
    link.href = url; link.download = name;
    link.textContent = T('save_image_hint', '画像を長押し／右クリックで保存');
    link.style.cssText = 'color:#fff;font-weight:700;text-decoration:underline;';
    var close = document.createElement('button');
    close.type = 'button';
    close.textContent = T('close', '閉じる');
    close.style.cssText = 'padding:10px 24px;border:0;border-radius:999px;background:#fff;font-weight:700;cursor:pointer;';
    close.addEventListener('click', function () { if (ov.parentNode) ov.parentNode.removeChild(ov); });
    ov.addEventListener('click', function (ev) { if (ev.target === ov) close.click(); });
    ov.appendChild(img); ov.appendChild(link); ov.appendChild(close);
    document.body.appendChild(ov);
  }

  /* ---------- inputs / storage ---------- */
  function syncInputs(root) {
    var state = stateOf(root);
    [['name', state.name], ['phone', state.phone], ['message', state.message]].forEach(function (pair) {
      $$(root, '[data-kz-input="' + pair[0] + '"]').forEach(function (el) { el.value = pair[1] || ''; });
    });
  }
  function storageKey(root) { return 'kizunatag-' + VERSION + '-' + species(root); }
  function saveState(root) { try { localStorage.setItem(storageKey(root), JSON.stringify(stateOf(root))); } catch (e) { } }
  function restoreState(root) {
    try {
      var raw = localStorage.getItem(storageKey(root));
      if (!raw) return;
      var saved = JSON.parse(raw), state = stateOf(root);
      ['product', 'material', 'size', 'font', 'name', 'phone', 'message'].forEach(function (k) { if (k in saved) state[k] = saved[k]; });
      if (saved.sel) { Object.keys(state.sel).forEach(function (c) { if (c in saved.sel) state.sel[c] = saved.sel[c]; }); }
    } catch (e) { }
  }

  /* ---------- dispatch ---------- */
  function dispatch(root, action) {
    var state = stateOf(root);
    if (!state) return;
    if (action.type === 'tab') { state.active = action.key; }
    else if (action.type === 'select') {
      var cat = action.cat;
      if (cat === 'product' || cat === 'material' || cat === 'size' || cat === 'font') state[cat] = action.id;
      if (cat === 'product' && catalog().normalize) catalog().normalize(state);
      else if (state.sel.hasOwnProperty(cat)) {
        state.sel[cat] = Number(action.id) || 0;
        if (cat === 'face') {
          if (state.faceMode === 'photo') { state.faceMode = 'preset'; clearPhoto(state); }
          if (faceHasOwnPattern(root)) state.sel.pattern = 0; /* 柄入りお顔に切替時は追加模様を解除 */
        }
        if (cat === 'pattern' && faceHasOwnPattern(root)) state.sel.pattern = 0; /* 柄入りお顔には模様を重ねない */
      }
    } else if (action.type === 'faceMode') {
      if (action.key === 'photo') { state.faceMode = 'photo'; state.active = 'face'; }
      else { state.faceMode = 'preset'; clearPhoto(state); }
    } else if (action.type === 'photoFile') {
      clearPhoto(state);
      state.photoFile = action.file;
      state.photoPreviewUrl = action.file ? URL.createObjectURL(action.file) : '';
    } else if (action.type === 'photoError') {
      state.photoError = action.message || '';
    } else if (action.type === 'photoRemove') {
      clearPhoto(state);
    } else if (action.type === 'accept') {
      state.accepted = !!action.value;
    } else if (action.type === 'input') {
      state[action.key] = String(action.value || '').slice(0, action.max || 100);
    } else if (action.type === 'reset') {
      clearPhoto(state);
      initState(root); syncInputs(root);
    }
    render(root);
  }

  /* ---------- events ---------- */
  function isRasterRoot(root) { return !!(root && SPECS[species(root)]); }
  function flash(el, msg) { var old = el.textContent; el.textContent = msg; setTimeout(function () { el.textContent = old; }, 1400); }
  function bindDelegates() {
    document.addEventListener('click', function (event) {
      var root = event.target.closest('[data-kz-maker]');
      if (!root || !isRasterRoot(root)) return;
      var el = event.target.closest('[data-kz-tab],[data-kz-select],[data-kz-save],[data-kz-png],[data-kz-cart],[data-kz-reset],[data-kz-face-mode],[data-kz-photo-change],[data-kz-photo-remove]');
      if (!el || !root.contains(el)) return;
      if (el.hasAttribute('data-kz-tab')) { dispatch(root, { type: 'tab', key: el.getAttribute('data-kz-tab') }); return; }
      if (el.hasAttribute('data-kz-face-mode')) { dispatch(root, { type: 'faceMode', key: el.getAttribute('data-kz-face-mode') }); return; }
      if (el.hasAttribute('data-kz-photo-change')) { var fi = root.querySelector('[data-kz-photo-input]'); if (fi) fi.click(); return; }
      if (el.hasAttribute('data-kz-photo-remove')) { dispatch(root, { type: 'photoRemove' }); return; }
      if (el.hasAttribute('data-kz-select')) { dispatch(root, { type: 'select', cat: el.getAttribute('data-cat'), id: el.getAttribute('data-id') }); return; }
      if (el.hasAttribute('data-kz-save')) { saveState(root); flash(el, T('saved', '保存しました')); return; }
      if (el.hasAttribute('data-kz-reset')) { dispatch(root, { type: 'reset' }); return; }
      if (el.hasAttribute('data-kz-png')) { downloadPng(root, el); return; }
      if (el.hasAttribute('data-kz-cart')) { addToCart(root).catch(function () { alert(T('alert_cart_failed', 'カートに追加できませんでした。入力内容をご確認のうえ、もう一度お試しください。')); }); return; }
    });
    document.addEventListener('input', function (event) {
      var root = event.target.closest('[data-kz-maker]');
      if (!root || !isRasterRoot(root)) return;
      var el = event.target, key = el.getAttribute && el.getAttribute('data-kz-input');
      if (!key) return;
      var max = key === 'name' ? 10 : key === 'phone' ? 15 : 24;
      dispatch(root, { type: 'input', key: key, value: el.value, max: max });
    });
    document.addEventListener('change', function (event) {
      var root = event.target.closest('[data-kz-maker]');
      if (!root || !isRasterRoot(root)) return;
      var el = event.target;
      if (el.hasAttribute && el.hasAttribute('data-kz-photo-input')) {
        var f = el.files && el.files[0];
        if (!f) return;
        if (PHOTO_TYPES.indexOf(f.type) === -1) { dispatch(root, { type: 'photoError', message: T('photo_type_err', '対応形式は JPG / PNG / WEBP です。') }); el.value = ''; return; }
        if (f.size > PHOTO_MAX_BYTES) { dispatch(root, { type: 'photoError', message: T('photo_size_err', '画像サイズは10MB以内にしてください。') }); el.value = ''; return; }
        dispatch(root, { type: 'photoFile', file: f });
        return;
      }
      if (el.hasAttribute && el.hasAttribute('data-kz-accept')) {
        dispatch(root, { type: 'accept', value: el.checked });
      }
    });
  }

  function init(root) {
    if (!isRasterRoot(root)) return;
    initState(root); restoreState(root);
    if (catalog().normalize) catalog().normalize(stateOf(root));
    syncInputs(root); render(root);
  }

  window.__KIZUNATAG_RASTER__ = {
    init: init, dispatch: dispatch, stateOf: stateOf,
    composePng: composePng, composeEngravingPng: composeEngravingPng, composeCutPng: composeCutPng,
    isRasterRoot: isRasterRoot,
    generateProductionId: generateProductionId, buildDesignSummary: buildDesignSummary,
    generatePreviewBlob: generatePreviewBlob, getOriginalPhotoFile: getOriginalPhotoFile,
    uploadProductionImages: uploadProductionImages, orderProperties: orderProperties
  };

  document.addEventListener('DOMContentLoaded', function () {
    bindDelegates();
    document.querySelectorAll('[data-kz-maker]').forEach(init);
  });
})();
